import type { LayoutOptionsDTO, UserSettingsDTO } from '@clanfire/shared';
import { deriveThemeVars, themeColorsFor } from './themes';

/**
 * What public/theme-init.js reads to paint the right look before the app
 * loads: whether it's dark (or follows the system), the colour tokens of any
 * theme that isn't Light or Dark, the layout's spacing, and your custom CSS.
 */
export const APPEARANCE_KEY = 'clanfire:appearance';

/**
 * Safe mode: opening ClanFire with ?safe in the address leaves your custom CSS
 * off until the next reload, so CSS that hides the settings can't lock you out.
 */
export const SAFE_MODE = typeof location !== 'undefined' && new URLSearchParams(location.search).has('safe');

const CUSTOM_CSS_ID = 'clanfire-custom-css';

let systemQuery: MediaQueryList | null = null;
let systemListener: (() => void) | null = null;
let appliedVars: string[] = [];

function setDark(dark: boolean, surface?: string) {
  document.documentElement.classList.toggle('dark', dark);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', surface ?? (dark ? '#1b1916' : '#f5ead8'));
}

/** Puts CSS custom properties on <html> (they win over the .dark / :root ones in index.css). */
function setVars(vars: Record<string, string>) {
  const style = document.documentElement.style;
  for (const name of appliedVars) style.removeProperty(name);
  appliedVars = Object.keys(vars);
  for (const [name, value] of Object.entries(vars)) style.setProperty(name, value);
}

/** The spacing and width parts of a layout, as CSS custom properties (see index.css). */
export function layoutVars(layout: LayoutOptionsDTO): Record<string, string> {
  return {
    '--cf-message-gap': { TIGHT: '0.5rem', NORMAL: '1.125rem', ROOMY: '1.75rem' }[layout.spacing],
    '--cf-chat-width': layout.chatWidth === 'READABLE' ? '52rem' : 'none',
  };
}

/** Avatar sizes in chat for each layout setting: [cozy, bubbles] in px. */
export const AVATAR_SIZES: Record<LayoutOptionsDTO['avatarSize'], [number, number]> = {
  SMALL: [32, 28],
  MEDIUM: [42, 34],
  LARGE: [52, 42],
};

/** Your custom CSS, last in <head> so it wins over the app's own styles. */
function setCustomCss(css: string | null) {
  let el = document.getElementById(CUSTOM_CSS_ID) as HTMLStyleElement | null;
  if (!css) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = CUSTOM_CSS_ID;
  }
  if (el.textContent !== css) el.textContent = css;
  // (Re)appended so it stays after anything added since.
  if (el !== document.head.lastElementChild) document.head.appendChild(el);
}

/** The custom CSS that should be on screen: none in safe mode or while it's switched off. */
export function activeCustomCss(settings: Pick<UserSettingsDTO, 'customCss' | 'customCssEnabled'>): string | null {
  return !SAFE_MODE && settings.customCssEnabled && settings.customCss.trim() ? settings.customCss : null;
}

type AppearanceSettings = Pick<UserSettingsDTO, 'theme' | 'customTheme' | 'fontScale' | 'reducedMotion' | 'layout' | 'customCss' | 'customCssEnabled'>;

/** Applies theme, layout spacing, custom CSS, font scale and motion preferences to the document. */
export function applyAppearance(settings: AppearanceSettings) {
  if (systemQuery && systemListener) systemQuery.removeEventListener('change', systemListener);
  systemQuery = null;
  systemListener = null;

  const colors = themeColorsFor(settings.theme, settings.customTheme);
  const themeVars = colors ? deriveThemeVars(colors) : null;
  let dark: boolean;
  if (colors) {
    dark = colors.base === 'DARK';
    setDark(dark, colors.colors.background);
  } else if (settings.theme === 'SYSTEM') {
    systemQuery = window.matchMedia('(prefers-color-scheme: dark)');
    systemListener = () => setDark(systemQuery!.matches);
    systemQuery.addEventListener('change', systemListener);
    dark = systemQuery.matches;
    setDark(dark);
  } else {
    // A custom theme picked with no colours saved falls back to Dark.
    dark = settings.theme !== 'LIGHT';
    setDark(dark);
  }
  const spacing = layoutVars(settings.layout);
  setVars({ ...themeVars, ...spacing });
  const css = activeCustomCss(settings);
  setCustomCss(css);

  try {
    localStorage.setItem(
      APPEARANCE_KEY,
      // The custom CSS is kept even in safe mode, so the next normal load has it.
      JSON.stringify({ system: settings.theme === 'SYSTEM', dark, vars: { ...themeVars, ...spacing }, css: settings.customCssEnabled ? settings.customCss : null }),
    );
  } catch {
    /* storage unavailable (private mode) — the look still applies for this session */
  }

  document.documentElement.style.setProperty('--clanfire-font-scale', String(settings.fontScale / 100));
  document.documentElement.classList.toggle('reduce-motion', settings.reducedMotion);
}

/** On logout: your custom CSS isn't left behind for whoever signs in next on this browser. */
export function clearCustomCss() {
  setCustomCss(null);
  try {
    const saved = JSON.parse(localStorage.getItem(APPEARANCE_KEY) ?? 'null') as { css?: string | null } | null;
    if (saved?.css) localStorage.setItem(APPEARANCE_KEY, JSON.stringify({ ...saved, css: null }));
  } catch {
    /* nothing saved, or storage unavailable */
  }
}

/** Shows custom CSS right away (while you type), without touching anything else. */
export function previewCustomCss(css: string | null) {
  setCustomCss(SAFE_MODE ? null : css);
}
